"use client"

import { getFixedT } from "@/languages/i18n"
import React, { ReactNode, useEffect, useRef, useState } from "react"

type Option = {
  label: ReactNode
  value: string
}

type LocationSelectProps = {
  value?: string
  onChange?: (value?: string) => void
  options: Option[]
  placeholder?: string
  loading?: boolean
  className?: string
  locale?: string
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  loading = false,
  className = "",
  locale,
}) => {
  const t = getFixedT(locale)
  const resolvedPlaceholder = placeholder ?? t("Select a branch")
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const selectedIndex = options.findIndex(o => o.value === value)
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setHighlightIndex(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (open) {
      setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0)
    }
  }, [open, selectedIndex])

  const handleSelect = (index: number) => {
    const opt = options[index]
    if (!opt) return
    onChange?.(opt.value)
    setOpen(false)
    setHighlightIndex(null)
    buttonRef.current?.focus()
  }

  const disabled = loading || options.length === 0

  return (
    <div ref={rootRef} className={`relative w-full sm:w-auto ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        className={`group flex items-center w-full rounded-lg text-base min-w-0
          outline-none transition
          ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
          ${
            open
              ? "border border-blue-500 bg-gray-50"
              : "border border-transparent bg-gray-100 hover:border-[rgb(179, 179, 179)]"
          }`}
      >
        <div className="flex-1 min-w-0 h-[52px] text-left flex items-center px-[10px] py-[8px]">
          <span className={`font-normal text-base truncate ${selectedOption ? "text-[#333333]" : "text-[#808080]"}`}>
            {selectedOption ? selectedOption.label : resolvedPlaceholder}
          </span>
        </div>

        {/* vạch dọc */}
        <span className="pointer-events-none w-[1px] h-9 bg-gray-300 shrink-0" />

        {/* icon hoặc loading */}
        <span className="pointer-events-none p-2 shrink-0">
          <svg
            height="20"
            width="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
            className={`transition-colors duration-200 ${
              open ? "text-[#999999]" : "text-[#CCCCCC] group-hover:text-[#999999]"
            }`}
          >
            <path
              d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div
          className="absolute z-10 mt-1 w-full overflow-visible rounded-lg border 
                     border-gray-200 bg-white shadow-lg"
        >
          <ul className="max-h-60 overflow-auto py-1">
            {options.map((opt, index) => {
              const selected = opt.value === value
              const isFirst = index === 0
              const highlighted = highlightIndex === index

              let cls = "cursor-pointer px-4 py-2.5 text-base select-none transition-colors"

              if (selected) {
                cls += " bg-[#2684FF] text-white"
              } else if (highlighted) {
                cls += " bg-gray-50 text-gray-800"
              } else {
                cls += " text-gray-700 hover:bg-[#deebff]"
              }

              return (
                <li
                  key={opt.value}
                  className={cls}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onClick={() => handleSelect(index)}
                >
                  {opt.label}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

export default LocationSelect
